import React from 'react';
import { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { ProfileUpdateWizardStepper } from '../ProfileUpdateWizardStepper';

interface ProfileUpdateFormProps {
    collaboratorId: string;
    initialData?: CollaboratorFormData;
    currentStep: number;
    updateStatus?: any;
    control: Control<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    isSubmitting: boolean;
    onSubmit: () => Promise<void>;
    onCancel?: () => void;
    onPrevStep?: () => void;
    onNoBmgAccountChange?: (hasNoBmgAccount: boolean) => void;
}

const ProfileUpdateForm: React.FC<ProfileUpdateFormProps> = ({
    collaboratorId,
    initialData,
    currentStep,
    updateStatus,
    control,
    errors,
    setValue,
    watch,
    isSubmitting,
    onSubmit,
    onCancel,
    onPrevStep,
    onNoBmgAccountChange,
}) => {
    // Handler para o botão - chama diretamente o onSubmit do pai
    const handleButtonClick = async () => {
        await onSubmit();
    };

    const isLastStep = currentStep === 6;

    // Compute flags
    const showBack = currentStep >= 0 && currentStep <= 6;
    const showSave = currentStep !== 7;

    // Criar elemento de botões se necessário
    const navigationButtons = showBack || showSave ? (
        <>
            {/* Botão Voltar ou placeholder para manter alinhamento */}
            {showBack && onPrevStep ? (
                <button
                    type="button"
                    onClick={onPrevStep}
                    className="px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium text-gray-700 bg-gray-200 border border-transparent rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Voltar
                </button>
            ) : (
                <span />
            )}

            {/* Botão Salvar / Continuar */}
            {showSave ? (
                <button
                    type="button"
                    onClick={handleButtonClick}
                    disabled={isSubmitting}
                    className="px-3 py-2 sm:px-4 sm:py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                >
                    <span className="hidden sm:inline">
                        {isLastStep ? (isSubmitting ? 'Finalizando...' : 'Finalizar') : (isSubmitting ? 'Salvando...' : 'Salvar e Continuar')}
                    </span>
                    <span className="sm:hidden">
                        {isLastStep ? (isSubmitting ? 'Finalizando...' : 'Finalizar') : (isSubmitting ? 'Salvando...' : 'Continuar')}
                    </span>
                </button>
            ) : null}
        </>
    ) : undefined;

    return (
        <div className="min-h-screen bg-gray-50 mt-2 sm:mt-4">
            <ProfileUpdateWizardStepper
                updateStatus={updateStatus || initialData?.updatedStatus}
                currentStepOverride={currentStep}
                context="PROFILE_UPDATE"
                collaboratorId={collaboratorId}
                collaboratorName={initialData?.name || ''}
                collaboratorEmail={initialData?.email || initialData?.personalEmail || ''}
                collaboratorPhotoUrl={initialData?.photoUrl}
                control={control}
                errors={errors}
                setValue={setValue}
                watch={watch}
                isSubmitting={isSubmitting}
                navigationButtons={navigationButtons}
                onNoBmgAccountChange={onNoBmgAccountChange}
            />
        </div>
    );
};

export default ProfileUpdateForm;